import { $axios } from 'boot/axios';
import handleError from 'src/helpers/handleError';
import { cloneObject } from 'src/helpers/objectHelper';

const initialState = () => ({
  documents: [],
  single: undefined,
  book: undefined,
  bookPreview: undefined,
  chapters: [],
  chapterTemplate: undefined,
  chapterCreating: false,
  model: undefined,
});

const transformFormToModel = (fieldsSchema, form) => {
  const result = { data: { meta: {} } };
  Object.keys(fieldsSchema).forEach((item) => {
    if (fieldsSchema[item].isMeta === false) {
      result.data[item] = form[item];
    } else {
      result.data.meta[item] = form[item];
    }
  });
  result.data.templateType = form.templateType;
  result.documentsCount = 1;
  return result;
};

const actions = {
  async updateBookTrackingCodes({ commit }, { book, codes }) {
    const { data } = await $axios.put(`generator/books/${book.guid}/tracking-codes`, codes);
    commit('setBook', {
      ...cloneObject(book),
      trackingCodes: data.data.trackingCodes,
    });
  },
  async getOneByUrl({ commit }, { domain, documentGuid }) {
    try {
      const { data } = await $axios.get(`public/route?url=https://${domain}/doc/${documentGuid}.html`);
      const { entry, entryKey } = data.data;
      if (entryKey === 'doc') {
        commit('setSingle', entry);
      }
    } catch (error) {
      console.error(error);
    }
  },
  async getByOneByGuide({ commit }, { documentGuid }) {
    try {
      const { data } = await $axios.get(`/generator/documents/${documentGuid}`);
      commit('setSingle', data.data);
    } catch (error) {
      console.log(error);
    }
  },
  async setEmailChain({ commit }, { book, emailChainGuid }) {
    const { data } = await $axios.put(`generator/books/${book.guid}/email-chain`, { emailChainGuid });
    commit('setBook', {
      ...cloneObject(book),
      emailChain: data.data.emailChain,
    });
  },
  async updateOne({ commit, state }, form) {
    const { data } = await $axios.put(`generator/documents/${state.single.guid}`, form);
    commit('setSingle', data.data);
  },
  async updatePublicLinkStatus({ commit, state }, status) {
    const { data } = await $axios.put(`generator/documents/${state.single.guid}/publish`, { publish: status });
    commit('setSingle', data.data);
  },
  async getBook({ commit }, bookGuid) {
    // TODO: change public links
    const { data: bookData } = await $axios.get(`generator/books/${bookGuid}`);
    commit('setBook', bookData.data);
  },

  async getChapters({ commit, state }) {
    try {
      const chapterEndpoint = `generator/books/${state.book.guid}/chapters`;
      const promises = state.book.chapters
        .map((chapter) => $axios.get(`${chapterEndpoint}/${chapter.guid}`)
          .then(({ data }) => data.data));
      commit('setChapters', await Promise.all(promises));
    } catch (error) {
      console.error(error);
    }
  },
  async syncChapters({ state, commit }, chapters) {
    const { data } = await $axios.put(`generator/books/${state.book.guid}/chapters/sync`, {
      chapters: chapters.map(({
        guid, note, title, position,
      }) => ({
        guid, note, title, position,
      })),
    });
    const oldChaptersMap = state.model.reduce((accumulator, value) => ({ ...accumulator, [value.guid]: value }), {});
    const newChapters = data.data.map((it) => ({ ...oldChaptersMap[it.guid], ...it }));

    commit('setChapters', newChapters);
  },

  async updateChapterPosition({ commit, state }, { chapter, position }) {
    await $axios.put(`generator/books/${state.book.guid}/chapters/${chapter.guid}`, {
      ...chapter,
      position,
    });
    const { data: bookData } = await $axios.get(`generator/books/${state.book.guid}`);
    commit('setBook', bookData.data);
    const newOrder = bookData.data.chapters.reduce((accumulator, value) => ({
      ...accumulator,
      [value.guid]: value,
    }), {});
    const newChapters = state.chapters.map((value) => ({
      ...value,
      position: newOrder[value.guid].position,
    })).toSorted((a, b) => a.position - b.position);

    commit('setChapters', newChapters);
  },
  async updateChapterNote({ commit, state }, { chapter, note }) {
    const { data } = await $axios.put(`generator/books/${state.book.guid}/chapters/${chapter.guid}`, {
      ...chapter,
      note,
    });
    commit('setChaptersNote', { i: data.data.position, payload: data.data.note });
  },
  async updateBook({ commit, state }, book) {
    const form = {
      ...state.book,
      ...book,
    };
    delete form.emailChain;
    if (form.metaFields.coverImageFileGuid?.guid) {
      form.metaFields.coverImageFileGuid = form.metaFields.coverImageFileGuid?.guid;
    }
    const { data } = await $axios.put(`generator/books/${state.book.guid}`, form, {
      skipReplaceInterceptor: true,
    });
    commit('setBook', data.data);
  },
  async deleteChapter({ commit, state }, guid) {
    await $axios.delete(`generator/books/${state.book.guid}/chapters/${guid}`);

    const changeChapters = (array) => array.filter((item) => item.guid !== guid).map((item, index) => ({
      ...item,
      position: index,
    }));
    commit('setChapters', changeChapters(state.chapters));
    commit('setBook', {
      ...state.book,
      chapters: changeChapters(state.book.chapters),
    });
  },
  async createChapterToModel({ commit, state }, chapter) {
    const { data } = await $axios.post(`generator/books/${state.book.guid}/chapters/`, {
      ...chapter,
    });
    commit('setModel', state.model.toSpliced(chapter.position, 0, {
      ...data.data,
      position: chapter.position,
    }));
    return data.data;
  },
  async fetchChapterTemplate({ commit }) {
    const { data } = await $axios.get('generator/templates/bookschapter');
    commit('setChapterTemplate', data.data);
  },
  async createChapterGenerationToModel({ commit, state, dispatch }, chapter) {
    if (!state.chapterTemplate) { await dispatch('fetchChapterTemplate'); }

    const { fieldsSchema } = state.chapterTemplate;

    const formEntries = Object.entries({ ...state.book.generation, ...state.book.generation.meta }).map(([key, value]) => {
      if (key === 'title') { return ['bookTitle', value]; }
      if (key === 'description') { return ['bookDescription', value]; }
      return [key, value];
    });

    const payload = transformFormToModel(
      fieldsSchema,
      {
        ...Object.fromEntries(formEntries),
        ...chapter,
      },
    );
    payload.data.templateType = 'bookschapter';

    const { data } = await $axios.post('generator/generations', {
      ...payload,
      bookChapterGuid: chapter.guid,
      immediatelyStart: true,
    });
    // добавляется содержимое для новой главы и меняются индексы для всех остальных глав
    const model = state.model.map((item, index) => (item.guid === chapter.guid ? { ...item, generation: data.data } : { ...item, position: index }));
    commit('setModel', model);

    return data.data;
  },
  async createEmptyChapterGenerationToModel({ commit, state }, chapter) {
    const { data } = await $axios.post('generator/generations', {
      data: {
        templateType: 'bookschapter',
        title: chapter.title,
        meta: {
          note: null,
          bookTitle: state.book.title,
          bookDescription: state.book.title,
        },
        toneOfVoice: 'general',
        brandVoice: null,
      },
      documentsCount: 1,
      bookChapterGuid: chapter.guid,
      immediatelyStart: true,
    });

    const model = state.model.map((item, index) => (item.guid === chapter.guid ? { ...item, generation: data.data } : { ...item, position: index }));
    commit('setModel', model);

    return data.data;
  },
  async createDocumentToEmptyChapter({ commit, state }, { chapterGuid, generationGuid }) {
    const { data } = await $axios.post(`/generator/generations/${generationGuid}/documents`);

    const model = state.model.map(
      (item, index) => (item.guid === chapterGuid ? { ...item, generation: { ...item.generation, documents: [data.data] } } : { ...item, position: index }),
    );
    commit('setModel', model);

    return data.data;
  },
  async getBookPreviewBySlug({ commit }, {
    domain,
    bookSlug,
    readerAccessToken,
    preview,
  }) {
    try {
      let route = `public/route?url=https://${domain}/book/${bookSlug}`;
      if (preview) {
        route += '?preview=1';
      }
      const { data } = await $axios.get(route, {
        headers: {
          ReaderAccessToken: readerAccessToken || '',
        },
      });
      const { entry, entryKey } = data.data;
      if (entryKey === 'book') {
        commit('setBookPreview', entry);
      }
    } catch (error) {
      console.log(error);
      handleError(error);
    }
  },
};

const mutations = {
  setSingle(state, document) {
    state.single = document;
  },
  setChapterCreating(state, payload) {
    state.chapterCreating = payload;
  },
  setModel(state, payload) {
    state.model = payload;
  },
  setModelChapterContent(state, { guid, payload }) {
    const editedChapter = state.model.find((it) => it.guid === guid);
    editedChapter.generation.documents[0].content = payload;
  },
  setBook(state, payload) {
    state.book = payload;
  },
  setChapters(state, payload) {
    state.chapters = payload;
  },
  setChapterModelTitle(state, { chapterGuid, payload }) {
    state.model = state.model.map((it) => (it.guid !== chapterGuid ? it : { ...it, title: payload }));
  },
  setChapterTemplate(state, payload) {
    state.chapterTemplate = payload;
  },
  setBookPageStatus(state, { bookPageGuid, status }) {
    const bookPageIndex = state.book.pages.findIndex((page) => page.guid === bookPageGuid);
    state.book.pages[bookPageIndex].isPublished = status;
  },
  clear(state) {
    const clearState = initialState();
    /* eslint-disable-next-line */
    Object.keys(clearState).forEach((key) => state[key] = clearState[key]);
  },
  setBookPreview(state, payload) {
    state.bookPreview = payload;
  },
};

export default {
  namespaced: true,
  state: initialState,
  actions,
  mutations,
};
